import classNames from "classnames";
import { parse as optimisticParse } from "best-effort-json-parser";
import React, { ReactElement, useState, useCallback, ChangeEventHandler, useRef, useMemo, useEffect } from "react";
import { MainHeaderSearchProps, MainHeaderSearchState, MainHeaderSearchStateProps, BansheeRelatedResult } from "./MainHeaderSearch.types";
import { Portal } from "src/components/Portal";
import { appInsights } from "src/AppInsights";
import { Conditional } from "src/components/Conditional";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import { localize, localizeFormat, getTypeName } from "src/l10n";
import SearchShortcutLabel from "src/spintr/components/SearchShortcutLabel";
import { UnstyledButton, SpintrUser, setDisplayHeaderSearch } from "src/ui";
import { SpintrTypes } from "src/typings";
import { executeSearchAction, SearchResultObjectHit, ISearchParams, SearchResponse, loadCachedSearchResult, setSearchAiSummary, clearSearchResults, submitFeedback } from "src/search";
import { useDispatch, useSelector } from "react-redux";
import SpintrLoader from "src/ui/components/Loader";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { SmallBodyBolder, CaptionBolder, SmallBody } from "src/components/Text";
import { checksum, capitalizeFirstLetter, mixpanelTrack } from "src/utils";
import api from "src/spintr/SpintrApi";
import qs from "qs";
import { AxiosResponse } from "axios";
import { useLocation } from "react-router";
import { SearchLoader } from "../SearchLoader";
import { useWindow } from "@fluentui/react";
import { ISearchFeedback } from "src/search/types";

function getIconNameByType(type: Spintr.UberType): string {
    switch (type) {
    case SpintrTypes.UberType.Unknown:
        return "home-wifi";
    case SpintrTypes.UberType.Status:
        return "status";
    case SpintrTypes.UberType.NewsArticle:
        return "3dcube";
    case SpintrTypes.UberType.TextPage:
        return "note-text";
    case SpintrTypes.UberType.File:
        return "folder-2";
    default:
        return "message";
    }
}

/**
 * MainHeaderSearch component
 *
 * @param {MainHeaderSearchProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function MainHeaderSearch({
}: MainHeaderSearchProps): ReactElement | null {
    const dispatch = useDispatch();
    const location = useLocation();
    const _window = useWindow()

    const stateProps = useSelector<Spintr.AppState, MainHeaderSearchStateProps>(
        (state) => ({
            results: state.search.currentResults,
            aiResults: state.search.currentAiResults,
            isSearching: state.search.isLoadingResults,
            headers: state.search.currentHeaders,
            lastParams: state.search.lastSuccessfulParams,
            history: state.search.history,
            displayHeaderSearch: state.ui.displayHeaderSearch,
        }),
    );

    const [state, setState] = useState<MainHeaderSearchState>({
        queryText: "",
        typeId: 0,
    });

    const inputRef = useRef<HTMLInputElement>();

    const onDismiss = useCallback(() => {
        dispatch(clearSearchResults());
        dispatch(setDisplayHeaderSearch(false));
    }, [dispatch]);

    const onOverlayClicked = useCallback<React.MouseEventHandler<HTMLDivElement>>(
        (event) => {
            event.preventDefault();
            event.stopPropagation();
            onDismiss();
        }, [onDismiss]);

    const onSummaryExpandedToggleClicked = useCallback(
        () => setState((prevState) => ({
            ...prevState,
            aiSummary: {
                ...prevState.aiSummary,
                showAll: !prevState.aiSummary?.showAll,
            },
        })), [setState]);

    const onResultClicked = useCallback((item: Spintr.ISearchResult) => {
        if (!item || !stateProps.lastParams || !stateProps.headers) {
            return;
        }

        if (location.pathname === "/") {
            mixpanelTrack("Search item clicked");
        }

        const isInternal = item.url.startsWith(_window.location.origin) ||
                           item.url.startsWith("/");

        if (!isInternal) {
            window.open(item.url);
        }

        const feedback: ISearchFeedback = {
            objectId: item.id,
            query: stateProps.lastParams?.query || "",
            totalHits: stateProps.headers?.totalCount || 0,
        };

        setState((prevState) => ({
            ...prevState,
            queryText: "",
            typeId: 0,
            bansheeResults: undefined,
            aiSummary: {
                summary: undefined,
                generating: false,
                showAll: false,
            },
        }));

        submitFeedback(feedback);
        dispatch(setDisplayHeaderSearch(false));
    }, [stateProps.lastParams, stateProps.headers, location.pathname]);

    const generateAiSummary = useCallback(async (
        query: string,
        documents: Spintr.ISearchResult[],
        hashCode: number
    ) => {
        if (state.aiSummary?.generating) {
            return;
        }

        const allowedTypes: Spintr.UberType[] = [
            SpintrTypes.UberType.NewsArticle,
            SpintrTypes.UberType.TextPage,
            SpintrTypes.UberType.WikiArticle,
        ];

        const filteredDocs = (documents || [])
            .filter((doc) => allowedTypes.includes(doc.type))
            .slice(0, 5);

        if (filteredDocs.length === 0) {
            return;
        }

        setState((prevState) => ({
            ...prevState,
            aiSummary: {
                summary: undefined,
                generating: true,
                showAll: false,
            },
        }));

        const asteriskPattern = /(\*{2})/g;
        const abortController = new AbortController();

        let hasOpenStrongElement = false,
            insertOnLineBreak = [],
            summaryText = "";

        await fetchEventSource("/api/v1/insights/search-summary", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                documents: filteredDocs,
                query: query,
            }),
            signal: abortController.signal,

            onerror: (err) => {
                console.log(err);
                abortController.abort();
                setState((prevState) => ({
                    ...prevState,
                    aiSummary: {
                        ...prevState.aiSummary,
                        summary: undefined,
                        generating: false,
                    },
                }));
                throw err;
            },

            onmessage: (event) => {
                const text = event.data;
                
                // replace double asterisks with <strong> tags
                let formattedText = text;
                switch (text) {
                    case "":
                        formattedText = insertOnLineBreak.pop() || "<br />";
                        break;
                    case "---":
                        formattedText = "<hr />";
                        break;
                    case "##":
                        formattedText = "<h2>";
                        insertOnLineBreak.push("</h2>");
                        break;
                    case "###":
                        formattedText = "<h3>";
                        insertOnLineBreak.push("</h3>");
                        break;
                    default:
                        formattedText = text.replace(asteriskPattern, (_) => {
                            if (hasOpenStrongElement) {
                                hasOpenStrongElement = false;
                                return "</strong>";
                            } else {
                                hasOpenStrongElement = true;
                                return "<strong>";
                            }
                        });
                        break;
                }

                summaryText += text;
                console.log(summaryText);
                setState((prevState) => ({
                    ...prevState,
                    aiSummary: {
                        ...prevState.aiSummary,
                        summary: optimisticParse(summaryText),
                        generating: true,
                    },
                }));
            },

            onopen: async (response) => {
                if (response.ok) {
                    return;
                }

                throw new Error(`Failed to open connection: ${response.status} ${response.statusText}`);
            },

            onclose: () => {
                setState((prevState) => ({
                    ...prevState,
                    aiSummary: {
                        ...prevState.aiSummary,
                        summary: optimisticParse(summaryText),
                        generating: false,
                    }
                }));

                dispatch(setSearchAiSummary(optimisticParse(summaryText), hashCode));
            },
        });
    }, [setState, dispatch, state.aiSummary?.generating]);

    const executeBansheeSearch = useCallback(async (queryText: string) => {
        let response: AxiosResponse<BansheeRelatedResult> | undefined;

        try {
            response = await api.get<BansheeRelatedResult>(
                "/api/v1/insights/related",
                {
                    params: {
                        keywords: queryText
                            .split(" ")
                            .filter((word) => word.length > 0),
                    },
                    paramsSerializer: (params) => qs.stringify(
                        params,
                        { arrayFormat: "indices" },
                    ),
                },
            );
        } catch (error) {
            console.error(error);
            return;
        }

        if (!response) {
            return;
        }

        setState((prevState) => ({
            ...prevState,
            bansheeResults: response.data,
        }));
    }, [setState]);

    const executeSearch = useCallback(async () => {
        if (stateProps.isSearching || !state.queryText || state.queryText.length < 2) {
            return;
        }

        appInsights.trackEvent({
            name: "Search",
        });

        const searchParams: ISearchParams = {
            sort: 0,

            ...(stateProps.lastParams || {}),

            includeAiResults: !state.typeId,
            query: state.queryText,
            objectType: state.typeId,
            take: 15,
        };

        const code = checksum(searchParams);
        if (stateProps.history[code]) {
            const cachedEntry = stateProps.history[code];
            dispatch(loadCachedSearchResult(code));
            setState((prevState) => ({
                ...prevState,
                aiSummary: {
                    summary: cachedEntry.aiSummary,
                    generating: false,
                    showAll: false,
                },
            }));
            executeBansheeSearch(state.queryText);
            return;
        }

        const searchActionResult: any = await dispatch(
            executeSearchAction(searchParams),
        );

        if (searchActionResult.action?.type !== "SEARCH_EXECUTE_FULFILLED") {
            return;
        }

        if (location.pathname === "/") {
            mixpanelTrack("Search");
        }

        executeBansheeSearch(state.queryText);

        if (!!searchParams.objectType) {
            return; // Only do AI summary for the main search
        }

        let documents: Spintr.ISearchResult[] = [];
        const response = searchActionResult.value.data as SearchResponse;
        if (Array.isArray(response)) {
            documents = response as Spintr.ISearchResult[];
        } else {
            documents = response.aiResults || [];
            if (documents.length === 0) {
                documents = response.spintrResults || [];
            }
        }

        if (documents.length <= 0) {
            setState((prevState) => ({
                ...prevState,
                aiSummary: {
                    summary: undefined,
                    generating: false,
                    showAll: false,
                },
            }));
            return; // Nothing to summarize
        }

        // TODO: Add condition for AI Summary?
        generateAiSummary(state.queryText, documents, code);
    }, [
        state.queryText,
        state.typeId,
        dispatch,
        stateProps.lastParams,
        stateProps.isSearching,
        generateAiSummary,
        executeBansheeSearch,
        stateProps.history,
        location.pathname,
    ]);

    const onQueryTextChanged = useCallback<ChangeEventHandler<HTMLInputElement>>(
        (event) => setState((prevState) => ({
            ...prevState,
            queryText: event.target?.value || "",
            objectType: 0,
        })), [setState]);

    const onQueryInputKeyPressed = useCallback<React.KeyboardEventHandler<HTMLInputElement>>(
        (event) => event.stopPropagation(), []);

    const onQueryInputKeyReleased = useCallback<React.KeyboardEventHandler<HTMLInputElement>>(
        (event) => {
            if (event.keyCode === SpintrTypes.KeyCodes.Escape) {
                onDismiss();
                return;
            }

            if (event.keyCode !== SpintrTypes.KeyCodes.Enter) {
                return;
            }

            executeSearch();
        }, [executeSearch, onDismiss, setState]);

    const onRelatedTermClicked = useCallback((word: string) => {
        setState((prevState) => ({
            ...prevState,
            queryText: word,
        }));

        executeSearch();
    }, [executeSearch, setState]);

    const onTypeChanged = useCallback((type: any) => {
        if (type === undefined || type === null || isNaN(type)) {
            return;
        }

        setState((prevState) => ({
            ...prevState,
            typeId: type,
        }));
    }, [setState]);

    useEffect(() => {executeSearch();}, [state.typeId]);

    const placeholderText = useMemo(() => localize("Sok") + "...", []);

    const types = useMemo(
        () => stateProps.headers && stateProps.headers?.objectTypes
            ?  Object.keys(stateProps.headers.objectTypes)
                .filter((key) => !isNaN(+key))
                .map((key) => parseInt(key, 10))
                .filter((key) => key !== SpintrTypes.UberType.ChatMessage)
                .map((key) => ({
                    key,
                    selected: false,
                    text: key === SpintrTypes.UberType.Unknown
                        ? localize("Alla")
                        : capitalizeFirstLetter(getTypeName(key, {
                            case: "indefinite",
                            form: "plural",
                        })),
                    count: stateProps.headers?.objectTypes?.[key] || 0,
                }))
            : [],
        [stateProps.headers]);

    useEffect(() => {
        if (!stateProps.displayHeaderSearch) {
            return;
        }

        const de = _window.document.documentElement;
        const b = _window.document.body;

        b.classList.add("no-scroll");
        b.style.setProperty('--st', -(de.scrollTop) + "px");

        return () => {
            b.classList.remove("no-scroll");
        };
    }, [stateProps.displayHeaderSearch, _window]);

    if (!stateProps.displayHeaderSearch) {
        return null;
    }

    let results = (stateProps.results ?? []).length > 0
        ? stateProps.results
        : (stateProps.aiResults ?? []);

    const hasResults = results && results.length > 0;

    results = results
        .filter((result) => !state.aiSummary?.summary?.references?.length 
                         || !state.aiSummary?.summary?.references
                                .every((ref) => ref.documentId !== result.id));

    const sidebarHidden = stateProps.isSearching ||
                          !results ||
                          results?.length <= 0

    return (
        <div id="MainHeaderSearch" className={classNames({
            "sidebarHidden": sidebarHidden,
        })}>
            <Portal>
                <div id="MainHeaderSearch-overlay" 
                     role="presentation"
                     onClick={onOverlayClicked} />
            </Portal>
            <div className="MainHeaderSearch-content">
                <div className="left">
                    <div className="search-bar">
                        <Visage2Icon icon="message-question" />
                        <input aria-label={placeholderText}
                               className="query-input"
                               name="query"
                               onChange={onQueryTextChanged}
                               onKeyDown={onQueryInputKeyPressed}
                               onKeyUp={onQueryInputKeyReleased}
                               placeholder={placeholderText}
                               ref={inputRef}
                               type="text"
                               value={state.queryText} />
                        <SearchShortcutLabel />
                        <Conditional condition={stateProps.isSearching || state.aiSummary?.generating}>
                            <SearchLoader size={20}
                                          speed={100} />
                        </Conditional>
                        <Conditional condition={!stateProps.isSearching && !state.aiSummary?.generating}>
                            <UnstyledButton onClick={executeSearch}>
                                <Visage2Icon icon="arrow-up" type="bold" />
                            </UnstyledButton>
                        </Conditional>
                    </div>
                    <div className="results">
                        <Conditional condition={stateProps.isSearching}>
                            <SpintrLoader />
                        </Conditional>
                        <Conditional condition={!stateProps.isSearching && hasResults}>
                            <Conditional condition={!!state.aiSummary?.summary?.summary}>
                                <div className={classNames("ai-summary", {
                                    expanded: state.aiSummary?.showAll,
                                    generating: state.aiSummary?.generating,
                                })}>
                                    <SmallBody className="ai-summary-text">
                                        <Visage2Icon className="ai-summary-icon"
                                                     color="visageMidBlue"
                                                     icon="bard-fill"
                                                     type="custom" />
                                        <span dangerouslySetInnerHTML={{
                                            __html: state.aiSummary?.summary?.summary || ""
                                        }} />
                                        {(state?.aiSummary?.summary?.references || []).map((doc) => {
                                            let objectHit = stateProps.aiResults?.find(
                                                (hit) => doc.documentId === hit.id,
                                            );

                                            if (!objectHit) {
                                                objectHit = stateProps.results?.find(
                                                    (hit) => doc.documentId === hit.id,
                                                );
                                            }

                                            if (!objectHit) {
                                                return null;
                                            }

                                            return (
                                                <SearchResultObjectHit key={doc.documentId} item={{
                                                    ...objectHit,
                                                    caption: doc.keyExcerpt ||  objectHit.caption,
                                                }} />
                                            );
                                        }).filter((x) => x !== null)}
                                        <Conditional condition={state.aiSummary?.summary?.summary?.length > 230}>
                                            <div className="ai-summary-overlay" />
                                        </Conditional>
                                    </SmallBody>
                                    <Conditional condition={state.aiSummary?.summary?.summary?.length > 230}>
                                        <UnstyledButton className="ai-summary-expand-button"
                                                        onClick={onSummaryExpandedToggleClicked}>
                                            <SmallBodyBolder color="blue">
                                                {localize(state.aiSummary?.showAll
                                                    ? "VisaMindre"
                                                    : "VisaMer")}
                                            </SmallBodyBolder>
                                            <Visage2Icon color="visageMidBlue"
                                                         icon={state.aiSummary?.showAll
                                                            ? "arrow-up-2" 
                                                            : "arrow-down-1" } />
                                        </UnstyledButton>
                                    </Conditional>
                                </div>
                            </Conditional>
                            <Conditional condition={results?.length > 0}>
                                <ol className="list">
                                    {results?.slice(0, 5)?.map((result) => (
                                        <li className="item" key={`HeaderSearch.Result${result.id}`}>
                                            <SearchResultObjectHit item={result}
                                                                   onClick={onResultClicked} />
                                        </li>
                                    ))}
                                </ol>
                            </Conditional>
                        </Conditional>
                    </div>
                </div>
                <Conditional condition={!sidebarHidden}>
                    <div className="right">
                        <Conditional condition={!!state.bansheeResults}>
                            <div className="banshee-results">
                                <div className="related-people">
                                    <div className="related-people-header">
                                        <Visage2Icon color="visageMidBlue"
                                                     icon="bard-fill"
                                                     type="custom" />
                                        <SmallBodyBolder>
                                            {localizeFormat(
                                                "QUESTIONS_ABOUT_X",
                                                '"' + (stateProps.lastParams?.query || "") + '"',
                                            )}
                                        </SmallBodyBolder>
                                    </div>
                                    <ol className="experts-list">
                                        {state.bansheeResults?.experts?.slice(0, 3)?.map((result) => (
                                            <li className="item expert" key={`HeaderSearch.Result${result.id}`}>
                                                <div className="user">
                                                    <SpintrUser
                                                        onClick={onDismiss}
                                                        imageUrl={result.imageUrl}
                                                        name={result.name}
                                                        nameLink={result.url}
                                                        subText={result.departmentName}
                                                        personalName
                                                    />
                                                </div>
                                            </li>
                                        ))}
                                    </ol>
                                </div>
                            </div>
                        </Conditional>
                        <div className="classic-results">
                            <SmallBodyBolder className="classic-results-header">
                                {localize("Sokresultat")}
                            </SmallBodyBolder>
                            <ol className="classic-results-types">
                                {types.map((type) => (
                                    <li key={type.key} className={classNames("type", {
                                        active: state.typeId === type.key,
                                    })}>
                                        <UnstyledButton className="type-button"
                                                        onClick={onTypeChanged}
                                                        onClickData={type.key}>
                                            <Visage2Icon className="type-icon"
                                                         icon={getIconNameByType(type.key)} />
                                            <SmallBodyBolder className="type-text">
                                                {type.text}
                                            </SmallBodyBolder>
                                            <CaptionBolder className="type-count">
                                                {type.count}
                                            </CaptionBolder>
                                        </UnstyledButton>
                                    </li>
                                ))}
                            </ol>
                        </div>
                        <Conditional condition={(state.bansheeResults?.expandedSet?.length || 0) > 0}>
                            <div className="related-terms">
                                <SmallBodyBolder className="related-terms-header"
                                                 color="contentDark">
                                    {localize("RELATED_SEARCH_PHRASES")}
                                </SmallBodyBolder>
                                    <ul className="list">
                                        {state.bansheeResults?.expandedSet?.map((word: string) => (
                                            <li className="item related-word" key={word}>
                                                <UnstyledButton
                                                    className="clickable-word"
                                                    onClick={onRelatedTermClicked}
                                                    onClickData={word}
                                                >
                                                    <CaptionBolder color="contentDark">
                                                        {word.substring(0, 1).toUpperCase() + word.substring(1)}
                                                    </CaptionBolder>
                                                </UnstyledButton>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                        </Conditional>
                    </div>
                </Conditional>
            </div>
        </div>
    );
}

export default MainHeaderSearch;

